import * as React from 'react';
import classNames from 'clsx';
export const outOfBounds = (total, index) => {
  return index < 0 || index >= total;
};
export const moveUp = (total, index) => {
  return outOfBounds(total, index - 1) ? total - 1 : index - 1;
};
export const moveDown = (total, index) => {
  return outOfBounds(total, index + 1) ? 0 : index + 1;
};
const acceptMouseEvent = e => {
  return e.button === 0 && !e.ctrlKey && !e.metaKey;
};
class Item extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.handleMouseEnter = e => {
      this.props.onHighlight(this.props.index);
    };
    this.handleClick = e => {
      if (acceptMouseEvent(e)) {
        e.preventDefault();
        this.props.onSelect(this.props.index);
      }
    };
  }
  render() {
    const {
      entry,
      highlighted,
      renderItem
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      onMouseDown: e => {
        e.preventDefault();
      },
      onClick: this.handleClick,
      onMouseEnter: this.handleMouseEnter
    }, renderItem({
      entry,
      highlighted
    }));
  }
}

/**
 * `ScrollableMenu` is a higher-order component that allows the caller
 * to define the component to render for individual search result items, as
 * well as the empty-state component.
 */
class ScrollableMenu extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.container = void 0;
    this.highlighted = void 0;
    this.scrollRequest = void 0;
    this.containerRef = node => {
      this.container = node;
    };
    this.highlightedRef = node => {
      this.highlighted = node;
    };
  }
  componentDidMount() {
    this.scrollRequest = this.scroll();
  }
  componentDidUpdate() {
    this.scrollRequest = this.scroll();
  }
  componentWillUnmount() {
    if (this.scrollRequest) {
      cancelAnimationFrame(this.scrollRequest);
    }
  }

  /**
   * Scroll the highlighted entry into view if necessary.
   */
  scroll() {
    return requestAnimationFrame(() => {
      const container = this.container;
      if (!container) {
        return;
      }
      if (this.props.highlightedIndex < 0) {
        container.scrollTop = 0;
        return;
      }
      const highlighted = this.highlighted;
      if (!highlighted) {
        return;
      }
      const {
        offsetHeight,
        offsetTop
      } = highlighted;
      const top = offsetTop;
      const bottom = offsetHeight + offsetTop;
      const {
        scrollTop
      } = container;
      const containerBottom = scrollTop + container.offsetHeight;
      if (top < scrollTop) {
        container.scrollTop = top;
      } else if (bottom > containerBottom) {
        container.scrollTop = bottom - container.offsetHeight;
      }
    });
  }
  render() {
    const {
      className,
      entries,
      highlightedIndex,
      id,
      onHighlight,
      onSelect,
      renderEmpty,
      renderItem,
      style,
      renderFooter,
      'data-testid': testId
    } = this.props;
    if (!entries.length) {
      return renderEmpty ? renderEmpty() : /*#__PURE__*/React.createElement("div", null);
    }
    const items = entries.map((entry, ii) => {
      const highlighted = ii === highlightedIndex;
      return /*#__PURE__*/React.createElement("li", {
        "aria-selected": highlighted,
        id: `${id}-item-${ii}`,
        key: ii,
        ref: highlighted ? this.highlightedRef : null,
        role: "option"
      }, /*#__PURE__*/React.createElement(Item, {
        entry: entry,
        highlighted: highlighted,
        index: ii,
        onSelect: onSelect,
        onHighlight: onHighlight,
        renderItem: renderItem
      }));
    });
    return /*#__PURE__*/React.createElement("div", {
      ref: this.containerRef,
      className: classNames('ScrollableMenu', className),
      style: style,
      "data-testid": testId
    }, /*#__PURE__*/React.createElement("ul", {
      id: id,
      role: "listbox"
    }, items), renderFooter ? renderFooter() : null);
  }
}
export default ScrollableMenu;